import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  BarChart3, 
  MessageSquare, 
  Zap, 
  Brain,
  ArrowRight,
  Sparkles,
  Star,
  Copy,
  Check,
  Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { ConsentModal } from '../../components/ConsentModal';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CastOfTheDay() {
  const [cast, setCast] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/ai/cast-of-the-day`)
      .then(r => r.json())
      .then(d => setCast(d.cast))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cast) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(cast.body || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-[#8B5CF6]/10 to-[#06B6D4]/10 border border-[#8B5CF6]/20 rounded-2xl p-5 md:p-6" data-testid="cast-of-the-day">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-5 h-5 text-[#8B5CF6]" />
        <h3 className="text-white font-bold text-sm">Cast of the Day</h3>
        <span className="text-[10px] text-white/30 ml-auto">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      </div>
      <h4 className="text-white/90 text-sm font-medium mb-2 line-clamp-2">{cast.title}</h4>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#8B5CF6]/15 text-[#8B5CF6]">{cast.platform_label}</span>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#06B6D4]/15 text-[#06B6D4]">{cast.domain_label}</span>
        {cast.role_label && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">{cast.role_label}</span>}
      </div>
      <pre className="text-white/50 text-[11px] leading-relaxed whitespace-pre-wrap font-mono max-h-[100px] overflow-hidden mb-3">{(cast.body || '').slice(0, 300)}...</pre>
      <div className="flex items-center gap-2">
        <button onClick={handleCopy}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg bg-[#8B5CF6]/20 text-[#8B5CF6] hover:bg-[#8B5CF6]/30 border border-[#8B5CF6]/20 transition-all"
          data-testid="cotd-copy">
          {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Cast</>}
        </button>
        <Link to="/dashboard/store" className="text-white/30 text-[11px] hover:text-white/50 ml-auto">
          Explore 4.3M more <ArrowRight className="w-3 h-3 inline" />
        </Link>
      </div>
    </div>
  );
}

export default function DashboardOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);

  const checkConsent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/consent/status`, { credentials: 'include' });
      const data = await res.json();
      if (!data.signed && user) setShowConsent(true);
    } catch (err) { logger.error("Operation failed:", err); }
  }, [user]);

  const checkFeedback = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feedback/status`, { credentials: 'include' });
      if (res.ok) setFeedbackStatus(await res.json());
    } catch (err) { logger.error("Operation failed:", err); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/usage/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (err) { logger.error("Operation failed:", err); } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStats();
    checkConsent();
    checkFeedback();
  }, [fetchStats, checkConsent, checkFeedback]);

  const creditsUsed = user?.credits_total ? user.credits_total - user.credits_remaining : 0;
  const creditsPercentage = user?.credits_total ? (creditsUsed / user.credits_total) * 100 : 0;

  const metrics = [
    {
      label: 'Messages Analyzed',
      value: stats?.total_events || 0,
      icon: MessageSquare,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
    },
    {
      label: 'Rewrites Applied',
      value: stats?.action_stats?.rewrite?.count || 0,
      icon: Sparkles,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
    {
      label: 'Credits Remaining',
      value: user?.credits_remaining || 0,
      icon: Zap,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
    },
    {
      label: 'Brain Score',
      value: user?.brain_score || 0,
      icon: Brain,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
    },
  ];

  const recentActivity = [
    { platform: 'Gmail', action: 'Professional rewrite', time: '2h ago' },
    { platform: 'LinkedIn', action: 'Confident tone', time: '5h ago' },
    { platform: 'Slack', action: 'Assertive rewrite', time: 'Yesterday' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      {/* Consent Modal */}
      <ConsentModal
        isOpen={showConsent}
        onComplete={() => setShowConsent(false)}
      />

      {/* Feedback Banner */}
      {feedbackStatus?.should_show_prompt && !feedbackDismissed && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-amber-400" />
            <span className="text-white text-sm">You've used SendPI {feedbackStatus.pi_checks} times. Help us improve — takes 2 minutes.</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/feedback" className="px-4 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg text-sm hover:bg-amber-500/30">Give Feedback</Link>
            <button onClick={() => setFeedbackDismissed(true)} className="text-white/30 text-sm hover:text-white/60 px-2">Dismiss</button>
          </div>
        </div>
      )}

      {/* Welcome */}
      <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-white/5 rounded-2xl p-6 md:p-8">
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">
          Welcome, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-white/55">
          Your Brain has analyzed {stats?.total_events || 0} messages this month.
        </p>
      </div>

      {/* Cast of the Day */}
      <CastOfTheDay />

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 hover:border-white/10 transition-colors"
          >
            <div className={`w-10 h-10 rounded-xl ${metric.bgColor} flex items-center justify-center mb-4`}>
              <metric.icon className={`w-5 h-5 ${metric.color}`} />
            </div>
            <p className="text-white font-space text-3xl mb-1">{metric.value.toLocaleString()}</p>
            <p className="text-white/40 text-sm">{metric.label}</p>
          </div>
        ))}
      </div>

      {/* Credits Progress */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium">Credits Usage</h3>
          <Link to="/dashboard/usage">
            <Button variant="ghost" size="sm" className="text-white/55 hover:text-white">
              View details <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-white/55">{creditsUsed} used</span>
            <span className="text-white">{user?.credits_remaining || 0} / {user?.credits_total || 100}</span>
          </div>
          <Progress value={creditsPercentage} className="h-2 bg-white/10" />
          <p className="text-white/40 text-xs">
            Resets in 8 days
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Brain Insight */}
        <div className="bg-[#0E1830] border border-amber-500/20 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-amber-400" />
            <h3 className="text-white font-medium">Brain Insight This Week</h3>
          </div>
          <div className="space-y-3 text-white/70 text-sm">
            <p>You escalate in tone on Friday afternoons — 3 messages flagged.</p>
            <p>Your opening messages to new contacts score 94% warmth.</p>
          </div>
          <Link to="/dashboard/brain" className="inline-block mt-4">
            <Button variant="outline" size="sm" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded-full">
              View full report <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-medium mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((item) => (
              <div key={`${item.platform}-${item.action}-${item.time}`} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-white/55" />
                  </div>
                  <div>
                    <p className="text-white text-sm">{item.platform}</p>
                    <p className="text-white/40 text-xs">{item.action}</p>
                  </div>
                </div>
                <span className="text-white/40 text-xs">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-4">
        <Link to="/dashboard/usage">
          <Button variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-full">
            View usage details
          </Button>
        </Link>
        <Link to="/dashboard/subscription">
          <Button variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-full">
            Upgrade plan
          </Button>
        </Link>
        <Link to="/dashboard/extension">
          <Button variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-full">
            Set up extension
          </Button>
        </Link>
      </div>
    </div>
  );
}
